import { useEffect, useRef } from 'react';
import useAuthStore from '@/store/useAuthStore';
import useUiStore from '@/store/useUiStore';

const KEEPALIVE_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
// FEAT NO-LOGOUT-DURING-RECORDING (18 Set 2026): con registrazione attiva
// il token viene rinfrescato più spesso (4 min < TTL 15 min) così che il
// logout per token scaduto non possa capitare a metà percorso.
const KEEPALIVE_INTERVAL_RECORDING_MS = 4 * 60 * 1000;

/**
 * useSessionKeepalive
 *
 * Periodically calls forceRefreshToken() while the user is authenticated,
 * silently refreshing the access token before it expires.
 *
 * forceRefreshToken() esegue una chiamata diretta a /api/auth/refresh
 * SENZA il guard hydrationPromise (a differenza di initSession()),
 * permettendo refresh ripetuti senza hack o manipolazioni dello store.
 *
 * FEAT NO-LOGOUT-DURING-RECORDING: refresh immediato anche al ritorno in
 * primo piano (visibilitychange) — su mobile i timer JS vengono sospesi
 * con lo schermo spento e al resume il token può essere già scaduto.
 *
 * Canonical path: import { useSessionKeepalive } from '@/hooks/auth/useSessionKeepalive';
 */
export function useSessionKeepalive(): void {
  const isAuthenticated    = useAuthStore((s) => s.isAuthenticated);
  const forceRefreshToken  = useAuthStore((s) => s.forceRefreshToken);
  const recordingLock      = useUiStore((s) => s.recordingLock);
  const intervalRef        = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const intervalMs = recordingLock
      ? KEEPALIVE_INTERVAL_RECORDING_MS
      : KEEPALIVE_INTERVAL_MS;

    intervalRef.current = setInterval(async () => {
      // QUOTA NEON (14 Ott 2026): con l'app in background senza registrazione
      // attiva il refresh periodico è inutile — ogni chiamata a
      // /api/auth/refresh è una scrittura su Neon e ne impedisce lo
      // scale-to-zero. Al ritorno in primo piano il token viene comunque
      // rinfrescato (visibilitychange qui sotto) e ogni 401 è recuperato
      // dal retry trasparente di apiFetch.
      if (!recordingLock && document.visibilityState !== 'visible') return;
      try {
        await forceRefreshToken();
      } catch (err) {
        console.warn('[useSessionKeepalive] Token refresh failed:', err);
      }
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, forceRefreshToken, recordingLock]);

  // Refresh al ritorno in primo piano (lo schermo spento sospende i timer
  // JS su Android — al resume il token può essere già scaduto).
  useEffect(() => {
    if (!isAuthenticated) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        forceRefreshToken().catch(() => { /* silenzioso */ });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, forceRefreshToken]);
}
